import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Search, Loader2, AlertCircle } from 'lucide-react'
import { useLibros, useToggleLibroStatus, useDeleteLibro } from '../../hooks/useQrCodigo'
import { LibroCard } from '../../components/LibroCard'

export function LibrosListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError, error } = useLibros({ search, page, page_size: 12 })
  const toggleMutation = useToggleLibroStatus()
  const deleteMutation = useDeleteLibro()

  const handleToggle = (id: number) => {
    toggleMutation.mutate(id)
  }

  const handleDelete = (id: number) => {
    if (confirm('¿Está seguro de eliminar este libro? Se eliminarán todos sus códigos y registros.')) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">QR Código</h1>
        <Button
          className="bg-teal-800 hover:bg-teal-900 text-white"
          onClick={() => navigate('/libro-qr/crear')}
        >
          <Plus className="mr-2 h-4 w-4" /> Crear Libro
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar libro..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
          <AlertCircle className="h-10 w-10 text-red-500" />
          <p className="text-muted-foreground">
            Error al cargar los libros. {(error as Error)?.message || 'Intente nuevamente.'}
          </p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data?.results?.map((libro) => (
              <LibroCard
                key={libro.id}
                libro={libro}
                onToggleStatus={handleToggle}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {(!data?.results || data.results.length === 0) && (
            <div className="text-center py-12 text-muted-foreground">
              {search ? 'No se encontraron libros con ese criterio' : 'No hay libros creados aún'}
            </div>
          )}

          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Página {data.page} de {data.total_pages} ({data.count} libros)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === data.total_pages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
